import { MaggieHttp } from '../MaggieHttp';

/**
 * Thin client for the Snowflake-sync admin endpoints. Used to populate connection /
 * table / column dropdowns in the Field Automation config panel. The backend is in the
 * `snowflake-sync` Nest module (api PR #3615).
 *
 * Only the `sharedForesight` connection mode is surfaced in the UI today —
 * external-credential connections exist on the backend but the admin form filters
 * them out for now.
 */

export type SnowflakeConnectionMode = 'sharedForesight' | 'external';

export interface SnowflakeConnection {
  id: number;
  name: string;
  mode: SnowflakeConnectionMode;
  database: string | null;
  schema: string | null;
  warehouse: string | null;
  role: string | null;
  account: string | null;
  username: string | null;
  // `secretRef` is intentionally absent — the backend strips it from responses.
}

export type SnowflakeTableKind = 'TABLE' | 'VIEW';

export interface SnowflakeTable {
  name: string;
  kind: SnowflakeTableKind;
}

export interface SnowflakeColumn {
  name: string;
  /** Snowflake column type as reported by `DESCRIBE TABLE` (e.g. `VARCHAR(16777216)`, `NUMBER(38,0)`). */
  type: string;
}

export interface CreateSnowflakeConnectionPayload {
  name: string;
  mode: SnowflakeConnectionMode;
  database?: string;
  schema?: string;
  role?: string;
}

export async function createSnowflakeConnection(
  payload: CreateSnowflakeConnectionPayload
): Promise<SnowflakeConnection> {
  const { data } = await MaggieHttp.maggie.post<CreateSnowflakeConnectionPayload, SnowflakeConnection>(
    '/snowflake-sync/connections',
    payload
  );
  return data;
}

export async function fetchSnowflakeConnections(): Promise<SnowflakeConnection[]> {
  const { data } = await MaggieHttp.maggie.get<SnowflakeConnection[]>('/snowflake-sync/connections');
  return data;
}

export async function fetchSnowflakeTables(connectionId: number): Promise<SnowflakeTable[]> {
  const { data } = await MaggieHttp.maggie.get<SnowflakeTable[]>(
    `/snowflake-sync/connections/${connectionId}/tables`
  );
  return data;
}

export async function fetchSnowflakeColumns(connectionId: number, table: string): Promise<SnowflakeColumn[]> {
  const { data } = await MaggieHttp.maggie.get<SnowflakeColumn[]>(
    `/snowflake-sync/connections/${connectionId}/tables/${encodeURIComponent(table)}/columns`
  );
  return data;
}
